import {ActivityHeader} from '@github-ui/commenting/ActivityHeader'
import {ReactionViewerBase} from '@github-ui/reaction-viewer/ReactionViewerBase'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import type {IssueComment} from '../../page-data/payloads/issue-comments'
import type {ReactionContent} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import {useReactToIssueCommentMutation} from '../../mutations/use-react-to-issue-comment-mutation'
import {Button} from '@primer/react'
import {ChevronRightIcon} from '@primer/octicons-react'
import {type SelectionContext, selectQuoteFromComment} from '@github-ui/commenting/quotes'
import {useRef} from 'react'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useLayoutCanComment} from '../../stores/layout-selectors'

interface IssueCommentItemProps {
  comment: IssueComment
  pullRequestAuthorLogin: string
  canReact: boolean
  onQuoteReply: (quotedText: string) => void
}

export function IssueCommentItem({
  comment,
  pullRequestAuthorLogin,
  canReact,
  onQuoteReply,
}: IssueCommentItemProps): React.ReactElement | null {
  const {mutate: reactToCommentMutation} = useReactToIssueCommentMutation()
  const currentUserCanComment = useLayoutCanComment()

  const commentRef = useRef<HTMLDivElement>(null)

  if (!comment.repository) return null

  const onReact = (reaction: ReactionContent, viewerHasReacted: boolean) => {
    reactToCommentMutation({
      commentId: comment.id,
      reaction,
      viewerHasReacted,
    })
  }

  const handleQuoteClick = () => {
    const current = ssrSafeWindow?.getSelection()
    let currentSelection: SelectionContext | null = null

    if (current && current.anchorNode && current.rangeCount > 0) {
      currentSelection = {
        anchorNode: current.anchorNode,
        range: current.getRangeAt(0),
      }
    }

    const quotedText = selectQuoteFromComment(commentRef.current, currentSelection, comment.body)
    onQuoteReply(quotedText ?? comment.body)
  }

  return (
    <div id={`issue-comment-${comment.id}`} ref={commentRef} className="border rounded-2">
      <div className="pt-2 tmp-px-3">
        <ActivityHeader
          avatarUrl={comment.authorAvatarUrl || ''}
          comment={{
            id: comment.id,
            authorAssociation: comment.authorAssociation,
            createdAt: comment.createdAt,
            isHidden: comment.isHidden,
            minimizedReason: comment.minimizedReason,
            repository: comment.repository,
            url: comment.url,
          }}
          commentAuthorLogin={comment.authorLogin}
          commentSubjectType="pull request"
          commentSubjectAuthorLogin={pullRequestAuthorLogin}
          forceInlineAvatar
          isMinimized={false}
          noContainerStyles
          tooltipDirection="sw"
        />
      </div>
      <div className="tmp-px-3 py-2">
        <VerifiedHTMLDiv className="markdown-body mt-1 f5" html={comment.bodyHtml} />
        {comment.reactionGroups.length > 0 && (
          <div className="mt-2">
            <ReactionViewerBase
              reactionGroups={comment.reactionGroups}
              canReact={canReact && comment.viewerCanReact}
              onReact={onReact}
            />
          </div>
        )}
        {currentUserCanComment && (
          <Button
            className="mt-2 ml-n2"
            onClick={handleQuoteClick}
            size="small"
            trailingVisual={ChevronRightIcon}
            variant="invisible"
          >
            Quote reply
          </Button>
        )}
      </div>
    </div>
  )
}
